import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const CenteredSection = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 18px 0 0;
  background: #fff;
`;

const SectionContent = styled.div`
  width: min(1280px, calc(100% - 36px));
  margin: 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 3.2vw, 3rem);
  line-height: 1;
  color: #2f3640;
  font-weight: 800;
`;

const NewsBlock = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 56px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 38px;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 32%;
  transform: translateY(-50%);
  width: 64px;
  height: 64px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: rgba(0, 0, 0, 0.65);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.16);
  flex-shrink: 0;
  z-index: 2;

  &:hover {
    background: rgba(0, 0, 0, 0.85);
  }

  span {
    font-size: 38px;
    line-height: 1;
    font-weight: 700;
    margin-top: -5px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`;

const PrevButton = styled(ArrowButton)`
  left: -8px;
`;

const NextButton = styled(ArrowButton)`
  right: -8px;
`;

const CardsViewport = styled.div`
  overflow: hidden;
  width: 100%;
`;

const CardsTrack = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.count}, minmax(0, 1fr));
  gap: 16px;
`;

const NewsCard = styled.article`
  min-width: 0;
`;

const Thumb = styled.img`
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 22px 22px 0 0;
`;

const DateText = styled.p`
  margin: 8px 0 4px;
  font-size: 1rem;
  color: #595f69;
  font-weight: 500;
`;

const Headline = styled.h3`
  margin: 0 0 6px;
  font-size: clamp(1.05rem, 1.6vw, 1.4rem);
  line-height: 1.2;
  color: #2f3640;
  font-weight: 700;
`;

const ReadMore = styled(Link)`
  color: #5ba7af;
  font-size: 0.98rem;
  font-weight: 600;
  text-decoration: underline;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0 8px;
`;

const Section5 = () => {
  const [news, setNews] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    fetch('https://us-central1-inprojuy-f1173.cloudfunctions.net/app/noticias')
      .then((response) => response.json())
      .then((data) => {
        const sortedNews = [...data]
          .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
          .slice(0, 5);
        setNews(sortedNews);
      });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const visibleCards = isMobile ? 1 : 3;
  const maxStart = Math.max(0, news.length - visibleCards);
  const displayedNews = news.slice(startIndex, startIndex + visibleCards);

  useEffect(() => {
    if (news.length <= visibleCards) return;

    const interval = setInterval(() => {
      setStartIndex((prev) => (prev >= maxStart ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [news.length, visibleCards, maxStart]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const formatted = date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'long',
    });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  return (
    <CenteredSection>
      <SectionContent>
        <Header>
          <Title>Últimas noticias</Title>
        </Header>
        <NewsBlock>
          <PrevButton
            aria-label="Noticias anteriores"
            onClick={() => setStartIndex((prev) => (prev <= 0 ? maxStart : prev - 1))}
          >
            <span aria-hidden>‹</span>
          </PrevButton>
          <CardsViewport>
            <CardsTrack count={visibleCards}>
              {displayedNews.map((item) => (
                <NewsCard key={item.id}>
                  <Thumb src={item.imagenUrl} alt={item.titulo} />
                  <DateText>{formatDate(item.timestamp)}</DateText>
                  <Headline>{item.titulo}</Headline>
                  <ReadMore to={`/noti/${item.id}`}>Ver más</ReadMore>
                </NewsCard>
              ))}
            </CardsTrack>
          </CardsViewport>
          <NextButton
            aria-label="Noticias siguientes"
            onClick={() => setStartIndex((prev) => (prev >= maxStart ? 0 : prev + 1))}
          >
            <span aria-hidden>›</span>
          </NextButton>
        </NewsBlock>
        <Actions>
          <Link className="btn btn-outline-primary btn-sm" to="/noticias">
            Ver todas las noticias
          </Link>
        </Actions>
      </SectionContent>
    </CenteredSection>
  );
};

export default Section5;
